"use client";

import { Clock3, MapPin } from "lucide-react";
import type { PlannerPreferences } from "@/lib/planner/preferences";
import { requiredStopError } from "@/lib/planner/required-stop";

const input = "mt-1 min-h-[44px] min-w-0 w-full rounded-[10px] border border-border bg-white px-3 text-sm font-normal text-navy focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal";

export function TimedStopField({ prefs, bases, onChange }: {
  prefs: PlannerPreferences;
  bases: { id: string; label: string }[];
  onChange: (prefs: PlannerPreferences, field: string) => void;
}) {
  const stop = prefs.requiredStop;
  const change = (next: PlannerPreferences["requiredStop"]) => onChange({ ...prefs, requiredStop: next }, "requiredStop");
  const error = requiredStopError(prefs);
  return <section className="rounded-2xl border border-border-light bg-white p-4" aria-label="Timed stop">
    <div className="flex items-start gap-3">
      <Clock3 size={22} aria-hidden className="mt-1 shrink-0 text-teal" />
      <div><h2 className="font-heading text-lg font-bold text-navy">Somewhere to be?</h2>
        <p className="mt-1 text-sm text-text-muted">A lunch booking, a lesson or meeting friends. Add one place you must reach on time.</p></div>
    </div>
    <label className="mt-3 flex min-h-[44px] cursor-pointer items-center gap-3 text-sm font-semibold text-navy">
      <input type="checkbox" checked={Boolean(stop)} className="h-5 w-5 accent-teal" onChange={event => change(event.target.checked ? { nodeId: "", time: "12:30", pauseMinutes: 30 } : undefined)} />
      Add a timed stop
    </label>
    {stop && <div className="mt-2 grid grid-cols-2 gap-3">
      <label className="col-span-2 text-sm font-semibold text-navy"><span className="inline-flex items-center gap-1"><MapPin size={15} aria-hidden /> Where do you need to be?</span>
        <select className={input} value={stop.nodeId} onChange={event => change({ ...stop, nodeId: event.target.value })}>
          <option value="">Choose a mapped meeting point</option>
          {bases.map(base => <option key={base.id} value={base.id}>{base.label}</option>)}
        </select>
      </label>
      <label className="min-w-0 text-sm font-semibold text-navy">Be there by<input className={input} type="time" value={stop.time} onChange={event => change({ ...stop, time: event.target.value })} /></label>
      <label className="min-w-0 text-sm font-semibold text-navy">Time at stop<select className={input} value={stop.pauseMinutes} onChange={event => change({ ...stop, pauseMinutes: Number(event.target.value) })}>
        {[0, 15, 30, 45, 60].map(minutes => <option key={minutes} value={minutes}>{minutes ? `${minutes} minutes` : "Just passing through"}</option>)}
      </select></label>
      <p className="col-span-2 text-xs leading-relaxed text-text-muted">Choose the nearest mapped point and allow time for any walk to your venue. This replaces automatic lunch and coffee breaks. All times are local to the resort.</p>
      {error && <p role="status" className="col-span-2 text-sm text-error">{error}</p>}
    </div>}
  </section>;
}
